/**
 * Copyright (c) 2018 itemis AG - All rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Contributors:
 * 	andreas muelder - itemis AG
 *
 */
import { dia, shapes, ui, util, g } from "@clientio/rappid";
import { BaseNode, BaseView } from "./base/base-node";
import { ElementController } from "./base/element-controller";
import NamedElement from "./base/named-element";
import { PreferenceStore } from "../services/preference-store";
import RappidContext from "../rappid/rappid-context";
import { Statechart } from "./statechart";
import { State, StateView } from "./state";
import { NodeLabel } from "./base/labeled-node";
import { deselectAll, difference } from "./base/utils";
import { removeStaticAttributes } from "./base/utils";
import { activeView } from "../subdiagram-viewer";
import { showGhostOnNextInteraction } from "../ghost";
import { actionState } from "../rappid/action-state";

const Position = ui.Halo.HandlePosition;
export const DEFAULT_NAME_FONTSIZE = 10;
export const MIN_WIDTH = 60;
export const MIN_HEIGHT = 60;
export const NAME_BG_HEIGHT = 12.25;
export const NAME_BG_Y_OFFSET = 3.5;

export class Region extends BaseNode implements NamedElement {
  movingInSubdiagram = false;

  defaults() {
    return {
      ...super.defaults,
      type: "Region",
      size: {
        height: MIN_HEIGHT,
        width: MIN_WIDTH,
      },
      fixedRatio: false,
      embedable: true,
      linkable: true,
      attrs: {
        root: {
          magnetSelector: "body",
        },
        priority: {
          text: "",
          fontSize: 7,
          textVerticalAnchor: "middle",
          textAnchor: "start",
          refX: "2px",
          refY: "5px",
        },
        body: {
          refWidth: "100%",
          refHeight: "100%",
          fillOpacity: 1,
          strokeWidth: PreferenceStore.INSTANCE.theme.model.region.borderWidth,
          stroke: null,
          fill: null,
        },
        name: {
          textVerticalAnchor: "middle",
          textAnchor: "start",
          refX: "10px",
          refY: "10px",
          display: "inline",
          fontSize: DEFAULT_NAME_FONTSIZE,
          fontFamily: PreferenceStore.INSTANCE.theme.model.text.fontFamily,
          text: "",
          textWrap: {
            maxLineCount: 1,
            ellipsis: true,
            preserveSpaces: true,
            width: "calc(w - 20)",
          },
          fill: null,
        },
        namebg: {
          ref: "body",
          fill: "transparent",
          refY: "3.5px",
          refX: "10 px",
          stroke: "transparent",
          strokeWidth: 0.5,
          height: "12.25px",
          cursor: "grab",
          width: "calc(w - 20)",
        },
        autosize: {
          width: false,
          height: false,
        },
      },
    };
  }

  markup = [
    {
      tagName: "rect",
      selector: "body",
    },
    {
      tagName: "text",
      selector: "specification",
    },
    {
      tagName: "text",
      selector: "priority",
    },
    {
      tagName: "g",
      selector: "namegroup",
      children: [
        { tagName: "rect", selector: "namebg" },
        { tagName: "text", selector: "name" },
      ],
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialize(...args: any[]) {
    super.initialize(...args);
    const size = this.size();
    if (size.width < 0 || size.height < 0) {
      const autosizeWidth = size.width < 0;
      const autosizeHeight = size.height < 0;

      this.attr(["autosize", "width"], autosizeWidth, { silent: true });
      this.attr(["autosize", "height"], autosizeHeight, { silent: true });
    }
    this.size(Math.max(MIN_WIDTH, size.width), Math.max(MIN_HEIGHT, size.height), { silent: true });
  }

  getName(): string {
    return this.attr(["name", "text"]);
  }

  setName(name: string) {
    this.attr(["name", "text"], name);
  }

  getFillColor(): string {
    return this.attr(["body", "fill"]);
  }

  setFillColor(color: string | null) {
    this.attr(["body", "fill"], color);
    if (color === null) {
      this.removeAttr(["body", "fill"]);
    }
  }

  getBorderColor(): string {
    return this.attr(["body", "stroke"]);
  }

  setBorderColor(color: string | null) {
    this.attr(["body", "stroke"], color);
    if (color === null) {
      this.removeAttr(["body", "stroke"]);
    }
  }

  getNameColor(): string {
    return this.attr(["name", "fill"]);
  }

  setNameColor(color: string | null) {
    this.attr(["name", "fill"], color);
    if (color === null) {
      this.removeAttr(["name", "fill"]);
    }
  }

  getNameFontSize(): number {
    return this.attr(["name", "fontSize"]);
  }

  setNameFontSize(size: number) {
    this.attr(["name", "fontSize"], size);
  }

  getBorderWidth(): number {
    return this.attr(["body", "strokeWidth"]);
  }

  setBorderWidth(width: number) {
    this.attr(["body", "strokeWidth"], width);
  }

  index(value?: number): number {
    // setter
    if (value !== undefined) {
      this.attr(["priority", "text"], value >= 0 ? value : "");
    }

    // getter
    else {
      const index = this.attr(["priority", "text"]);
      if (index !== undefined) {
        return +index;
      }
    }
    return 0;
  }

  resetDefaults() {
    this.setFillColor(null);
    this.setNameColor(null);
    this.attr(["body", "strokeWidth"], PreferenceStore.INSTANCE.theme.model.region.borderWidth);
    this.setBorderColor(null);
    this.attr(["name", "fontSize"], DEFAULT_NAME_FONTSIZE);
  }

  unembed(cell: dia.Cell | dia.Cell[], opt?: dia.Graph.Options) {
    return super.unembed(cell, opt);
  }

  embed(
    cell: dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions> | dia.Cell<dia.Cell.Attributes, dia.ModelSetOptions>[],
    opt?: dia.Graph.Options
  ): this {
    // Dont re-embed a NodeLabel because they are already embeded See #442
    if (cell instanceof NodeLabel) {
      return this;
    }
    return super.embed(cell, opt);
  }

  toJSON() {
    const result = super.toJSON();
    result.attrs = difference(result.attrs, this.defaults().attrs);
    delete result.attrs.autosize;
    return removeStaticAttributes(result);
  }

  getMinSize(direction: dia.Direction): { width: number; height: number } {
    const regionBBox = this.getBBox();
    const cells = this.getEmbeddedCells();
    if (cells.length === 0) {
      return { width: MIN_WIDTH, height: MIN_HEIGHT };
    }
    const cellsBBox = this.graph.getCellsBBox(cells);

    switch (direction) {
      case "right": {
        return this.getMinSizeRight(cellsBBox, regionBBox);
      }
      case "left": {
        return this.getMinSizeLeft(cellsBBox, regionBBox);
      }
      case "bottom": {
        return this.getMinSizeBottom(cellsBBox, regionBBox);
      }
      case "top": {
        return this.getMinSizeTop(cellsBBox, regionBBox);
      }
      case "bottom-right": {
        const width = this.getMinSize("right").width;
        const height = this.getMinSize("bottom").height;
        return { width, height };
      }
      case "top-right": {
        const width = this.getMinSize("right").width;
        const height = this.getMinSize("top").height;
        return { width, height };
      }
      case "bottom-left": {
        const width = this.getMinSize("left").width;
        const height = this.getMinSize("bottom").height;
        return { width, height };
      }
      case "top-left": {
        const width = this.getMinSize("left").width;
        const height = this.getMinSize("top").height;
        return { width, height };
      }
      default: {
        return { width: MIN_WIDTH, height: MIN_HEIGHT };
      }
    }
  }

  private getMinSizeRight(cellsBBox: g.Rect, regionBBox: g.Rect) {
    const width = Math.max(MIN_WIDTH, cellsBBox.rightLine().bbox().x - regionBBox.x + RegionView.PADDING);
    return { width, height: 0 };
  }

  private getMinSizeLeft(cellsBBox: g.Rect, regionBBox: g.Rect): { width: number; height: number } {
    const width = Math.max(
      MIN_WIDTH,
      regionBBox.x + regionBBox.width - cellsBBox.leftLine().bbox().x + RegionView.PADDING
    );
    return { width, height: 0 };
  }

  private getMinSizeBottom(cellsBBox: g.Rect, regionBBox: g.Rect): { width: number; height: number } {
    const height = Math.max(MIN_HEIGHT, cellsBBox.bottomLine().bbox().y - regionBBox.y + RegionView.PADDING);
    return { width: 0, height };
  }

  private getMinSizeTop(cellsBBox: g.Rect, regionBBox: g.Rect): { width: number; height: number } {
    const height = Math.max(
      MIN_HEIGHT,
      regionBBox.y + regionBBox.height - cellsBBox.topLine().bbox().y + RegionView.PADDING + RegionView.TOP
    );
    return { width: 0, height };
  }

  getMaxSize(direction: dia.Direction): { width: number; height: number } {
    const parent = this.getParentCell() as State;
    if (!parent) {
      return { width: Infinity, height: Infinity };
    }
    const regions = parent.getEmbeddedCells().filter((c) => c instanceof Region);
    const regionIndex = regions.indexOf(this as Region);

    switch (direction) {
      case "right": {
        let rightRegionsWidth = 0; // right padding of state
        for (let i = regionIndex + 1; i < regions.length; i++) {
          rightRegionsWidth += regions[i].size().width;
        }
        const calculatedWidth =
          parent.getMaxSize("right").width -
          this.position({ parentRelative: true }).x -
          StateView.Padding -
          rightRegionsWidth;
        return { width: calculatedWidth, height: Infinity };
      }
      case "left": {
        let leftRegionsWidth = 0; // left padding of state
        for (let i = 0; i < regionIndex; i++) {
          leftRegionsWidth += regions[i].size().width;
        }
        const calculatedWidth =
          parent.getMaxSize("left").width -
          (parent.getBBox().width - this.position({ parentRelative: true }).x - this.size().width) -
          StateView.Padding -
          leftRegionsWidth;
        return { width: calculatedWidth, height: Infinity };
      }

      default: {
        return { width: Infinity, height: Infinity };
      }
    }
  }

  fitToChildren(opt?: {
    deep?: boolean;
    padding?: dia.Padding;
    expandOnly?: boolean;
    shrinkOnly?: boolean;
    terminator?: dia.Cell | dia.Cell.ID;
  }): this {
    const newOpt = opt || {};
    newOpt.padding = {
      top: RegionView.PADDING + RegionView.TOP,
      right: RegionView.PADDING,
      bottom: RegionView.PADDING,
      left: RegionView.PADDING,
    };
    return super.fitToChildren({ ...newOpt });
  }

  fitParent(opt?: {
    deep?: boolean;
    padding?: dia.Padding;
    expandOnly?: boolean;
    shrinkOnly?: boolean;
    terminator?: dia.Cell | dia.Cell.ID;
  }): this {
    const parentCell = this.getParentCell();
    if (parentCell !== null && parentCell.id === activeView.value?.model?.id) {
      return this;
    }
    return super.fitParent(opt);
  }
}

export class RegionView extends BaseView {
  static readonly PADDING = 3;
  static readonly TOP = 17;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  confirmUpdate(flag: number, opt: { [key: string]: any }): number {
    const INIT_FLAG = 64;
    if ((flag & INIT_FLAG) === INIT_FLAG) {
      this.applyAutosizeIfNeeded();
    }
    return super.confirmUpdate(flag, opt);
  }

  private applyAutosizeIfNeeded() {
    const region = this.model as Region;
    const size = region.size();
    const autosizeWidth = region.attr(["autosize", "width"]);
    const autosizeHeight = region.attr(["autosize", "height"]);

    if (autosizeWidth || autosizeHeight) {
      // Check if DOM is ready
      const nameText = this.vel.findOne("text[joint-selector='name']");
      if (!nameText) {
        // DOM not ready yet, schedule for next tick
        setTimeout(() => this.applyAutosizeIfNeeded(), 0);
        return;
      }

      const parent = region.getParentCell() as State;
      if (!parent) {
        // No parent yet, will be handled when embedded
        return;
      }

      // Recursively fit embedded content first (bottom-up)
      const embeddedStates = region.getEmbeddedCells().filter((c) => c instanceof State);
      embeddedStates.forEach((s) => {
        const stateView = this.paper.findViewByModel(s) as StateView;
        if (stateView) {
          // Check if embedded state has autosize regions (size 0)
          const autosizeRegions = (s as State).getEmbeddedRegions().filter((r) => {
            const rSize = r.size();
            return rSize.width === 0 || rSize.height === 0;
          });
          if (autosizeRegions.length > 0) {
            autosizeRegions.forEach((r) => {
              const regionView = this.paper.findViewByModel(r) as RegionView;
              regionView?.applyAutosizeIfNeeded();
            });
          }
        }
      });

      // Calculate required dimensions based on embedded content
      const currentSize = region.size();
      let newWidth = currentSize.width;
      let newHeight = currentSize.height;

      if (autosizeWidth) {
        const textWrap = region.attr("name/textWrap");
        region.attr("name/textWrap", null, { async: false });
        const nameWidth = nameText.getBBox().width + 21;
        region.attr("name/textWrap", textWrap, { async: false });

        const minSizeRight = region.getMinSize("right").width;
        newWidth = Math.max(minSizeRight, nameWidth);
      }

      if (autosizeHeight) {
        const minSizeBottom = region.getMinSize("bottom").height;
        newHeight = Math.max(minSizeBottom, MIN_HEIGHT);
      }

      region.size(newWidth, newHeight, { silent: true });
      this.update();

      parent.fitRegions();
    }
  }

  fitToContent(deep = false) {
    const region = this.model as Region;
    const states = region.getEmbeddedCells().filter((c) => c instanceof State);

    if (deep) {
      // call embedded cells first
      states.forEach((s) => {
        (this.paper.findViewByModel(s) as StateView)?.fitToContent();
      });
    }

    const textWrap = this.model.attr("name/textWrap");

    this.model.attr("name/textWrap", null, { async: false });
    const nameText = this.vel.findOne("text[joint-selector='name']");
    const nameWidth = nameText.getBBox().width + 21;
    const parent = region.getParentCell() as State;

    if (parent.isVertical()) {
      const heightBottom = Math.max(region.getMinSize("bottom").height);
      this.model.size(region.size().width, heightBottom, { direction: "bottom" });
      this.changeSize("bottom");

      const heightTop = Math.max(region.getMinSize("top").height);
      this.model.size(region.size().width, heightTop, { direction: "top" });
      this.changeSize("top");
    } else {
      const widthLeft = Math.max(region.getMinSize("left").width, nameWidth);
      this.model.size(widthLeft, region.size().height, { direction: "left" });
      this.changeSize("left");

      const widthRight = Math.max(region.getMinSize("right").width, nameWidth);
      this.model.size(widthRight, region.size().height, { direction: "right" });
      this.changeSize("right");
    }

    this.model.attr("name/textWrap", textWrap, { async: false });
    this.update();
  }

  changeSize(direction?: dia.OrthogonalDirection) {
    const state = this.model.getParentCell() as State;
    if (!state) {
      return;
    }
    if (state.getSubdiagram()) {
      return;
    }
    const regions = state.getEmbeddedCells().filter((r) => r instanceof Region);
    switch (direction) {
      case "right": {
        this.afterResizeRight(state, regions);
        break;
      }
      case "left": {
        this.afterResizeLeft(state, regions);
        break;
      }
      case "top": {
        this.afterResizeTop(state, regions);
        break;
      }
      case "bottom": {
        this.afterResizeBottom(state, regions);
        break;
      }
    }
  }

  private afterResizeRight(state: State, regions: Region[]) {
    const width = regions.reduce((sum, r) => sum + r.size().width, 0) + StateView.Padding * 2;
    state.size(width, state.size().height, { direction: "right" });

    let x = state.position().x + StateView.Padding;
    regions.forEach((r) => {
      r.position(x, r.position().y, { deep: true });
      x += r.size().width;
    });
  }

  private afterResizeLeft(state: State, regions: Region[]) {
    const width = regions.reduce((sum, r) => sum + r.size().width, 0) + StateView.Padding * 2;
    state.position(state.position().x + state.size().width - width, state.position().y);
    state.size(width, state.size().height);

    let x = state.position().x + state.size().width - width + StateView.Padding;
    regions.forEach((r) => {
      r.position(x, r.position().y, { deep: true });
      x += r.size().width;
    });
  }

  private afterResizeTop(state: State, regions: Region[]) {
    const offsetY = state.getEditorHeight() + StateView.MarginTop + StateView.Top;
    const height = regions.reduce((sum, r) => sum + r.size().height, 0) + offsetY + StateView.Padding * 2;
    state.position(state.position().x, state.position().y + state.size().height - height);
    state.size(state.size().width, height);

    let y = state.position().y + state.size().height - height + StateView.Padding;
    regions.forEach((r) => {
      r.position(r.position().x, y + offsetY, { deep: true });
      y += r.size().height;
    });
  }

  private afterResizeBottom(state: State, regions: Region[]) {
    const offsetY = state.getEditorHeight() + StateView.MarginTop + StateView.Top;
    const height = regions.reduce((sum, r) => sum + r.size().height, 0) + offsetY + StateView.Padding * 2;
    state.size(state.size().width, height);

    let y = state.position().y + offsetY + StateView.Padding;
    regions.forEach((r) => {
      r.position(r.position().x, y, { deep: true });
      y += r.size().height;
    });
  }
}

export class RegionController extends ElementController {
  constructor(view: dia.CellView, context: RappidContext) {
    super(view, context);
    view.model.on("change:parent", this.onChangeParent.bind(this));
    view.model.on("change:embeds", this.onChangeEmbeds.bind(this));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChangeEmbeds(element: dia.Element, embeds: any, opt: any) {
    if (actionState.isUndo) {
      return;
    }
    element.fitToChildren({ expandOnly: true, deep: true });

    const parent = element.getParentCell();
    if (parent) {
      parent.trigger("change:embeds", parent, embeds, opt);
    }
  }

  createFreetransform() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    const view = this.view as RegionView;
    const model = this.view.model as Region;
    const ft = new (class extends ui.FreeTransform {
      snaplines: ui.Snaplines = view.model.getParentCell() instanceof State ? _this.context.snaplines : null;

      constructor() {
        super({
          theme: "modern",
          cellView: view,
          allowRotation: false,
          resizeGrid: { width: 1, height: 1 },
          preserveAspectRatio: view.model.attributes["fixedRatio"],
          resizeDirections: _this.computeResizeDirections(),
          minWidth(cell, FreeTransform, direction) {
            const { width } = model.getMinSize(direction);
            return width;
          },
          minHeight(cell, FreeTransform, direction) {
            const { height } = model.getMinSize(direction);
            return height;
          },
          maxWidth(cell, FreeTransform, direction) {
            const { width } = model.getMaxSize(direction);
            return width;
          },
          maxHeight(cell, FreeTransform, direction) {
            const { height } = model.getMaxSize(direction);
            return height;
          },
          padding: 0,
        });

        this.addResizeListeners();
      }

      addResizeListeners() {
        const region = view.model as Region;
        this.listenTo(view.model.graph, "batch:stop", (data) => {
          const { freeTransform: cid, cell, relativeDirection, batchName } = data;
          if (cid !== this.cid) return;
          if (batchName !== "resize") return;
          if (region !== cell) return;

          // Disable autosize based on resize direction
          if (relativeDirection.includes("left") || relativeDirection.includes("right")) {
            region.attr(["autosize", "width"], false);
          }
          if (relativeDirection.includes("top") || relativeDirection.includes("bottom")) {
            region.attr(["autosize", "height"], false);
          }

          view.changeSize(relativeDirection);
          view.update();
        });
      }
    })();
    ft.render();
  }
  computeResizeDirections(): ui.FreeTransform.Directions[] {
    const region = this.view.model as Region;
    const parent = region.getParentCell();
    if (parent instanceof State && !parent.getSubdiagram()) {
      const parentState = parent as State;
      const orthogonalRegions = parentState.getEmbeddedRegions();
      const num = orthogonalRegions.length;
      const idx = orthogonalRegions.indexOf(region);
      // no resize handle for single region
      if (num === 1) {
        return [];
      }
      const direction = parentState.getLayoutDirection() || "horizontal";
      const isVertical = direction === "vertical";

      return isVertical ? ["bottom", "top"] : ["right", "left"];
    }
    return ["top", "bottom", "left", "right", "top-left", "top-right", "bottom-left", "bottom-right"];
  }

  onCellPointerDblClick(evt?: dia.Event) {
    const selector = evt?.target?.getAttribute("joint-selector");
    if (
      selector === "name" ||
      selector === "namebg" ||
      evt.target.getAttribute("class") === "v-line" ||
      selector === null
    ) {
      this.context.nameTextEditor.create(this.view, "name");
    }
  }

  getHaloConfig() {
    return [
      {
        name: "remove",
        position: Position.NW,
        events: { pointerdown: "removeElement" },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Remove the object",
            "data-tooltip-position": "right",
            "data-tooltip-padding": 25,
          },
        },
      },
    ];
  }

  onAdd() {
    this.updatePriorities();
  }

  onRemove() {
    this.updatePriorities();
  }

  onChangeParent() {
    this.updatePriorities();
  }

  private updatePriorities() {
    const sc = this.context.graph.getCells().find((c) => c instanceof Statechart) as Statechart;
    if (sc) sc.updateTopLevelRegionPriorities();
  }

  private newParent: StateView | null = null;
  private diff: { x: number; y: number } = { x: 0, y: 0 };
  private embeddedRegionDragging: boolean = false;
  private selected: boolean = false;

  onElementPointerDown(evt?: joint.dia.Event) {
    //Do not select region on middle mouse panning
    if (evt.button === 1) return;
    this.selected = true;
    const parent = this.view.model.getParentCell() as State;
    if (parent) {
      this.embeddedRegionSelect(parent, evt);
    }
  }

  onElementPointerMove(evt?: joint.dia.Event) {
    if (!this.selected) return;
    const parent = this.view.model.getParentCell() as State;
    if (parent && !parent.getSubdiagram()) {
      this.embeddedRegionMove(evt);
    }
  }

  onElementPointerUp(evt?: joint.dia.Event) {
    if (!this.selected) return;
    this.selected = false;
    const parent = this.view.model.getParentCell() as State;
    if (parent) {
      (this.view.model as Region).movingInSubdiagram = false;
      if (!parent.getSubdiagram()) {
        this.embeddedRegionDrop(parent, evt);
      }
    }
    this.updateFreetransform();
  }

  private embeddedRegionSelect(parent: State, evt: dia.Event) {
    if (parent.getSubdiagram()) {
      (this.view.model as Region).movingInSubdiagram = true;
    } else {
      this.view.preventDefaultInteraction(evt);
      showGhostOnNextInteraction(this.view.paper);

      this.newParent = parent.findView(this.view.paper) as StateView;
      const point = this.view.paper.clientToLocalPoint(evt.clientX, evt.clientY);
      const currentPosition = this.view.model.position();
      this.diff = { x: point.x - currentPosition.x, y: point.y - currentPosition.y };
    }
  }

  private embeddedRegionMove(evt: dia.Event) {
    const potentialNewParent = this.findNewParent(evt);

    if (!this.embeddedRegionDragging) {
      this.newParent?.highlight(this.newParent.el, { embedding: true });
      this.embeddedRegionDragging = true;
    } else if (potentialNewParent !== this.newParent) {
      this.newParent?.unhighlight(this.newParent?.el, { embedding: true });
      potentialNewParent?.highlight(potentialNewParent.el, { embedding: true });
      this.newParent = potentialNewParent;
    }
  }

  private embeddedRegionDrop(parent: State, evt: joint.dia.Event) {
    this.newParent = null;
    this.embeddedRegionDragging = false;

    const newParentView = this.findNewParent(evt);
    const parentView = parent.findView(this.view.paper) as StateView;

    if (!newParentView) {
      this.removeFromCompositeState(parent, parentView, evt);
    } else {
      if (newParentView === parentView) {
        this.reparent(parent, parentView, evt);
      } else {
        this.addToNewParent(parent, newParentView, evt);
      }
      this.updateZIndex(newParentView);
    }
  }

  private addToNewParent(parent: State, newParentView: StateView, evt: joint.dia.Event) {
    const point = this.view.paper.clientToLocalPoint(evt.clientX, evt.clientY);

    parent.unembed(this.view.model);
    newParentView.unhighlight(newParentView.el, { embedding: true });

    newParentView.model.embed(this.view.model);

    const regions = newParentView.model.getEmbeddedCells().filter((c) => c instanceof Region);
    const insertIndex = this.getInsertIndex(parent, regions, point);

    this.moveToIndex(insertIndex);

    if (newParentView === activeView.value) {
      const region = this.view.model as Region;
      region.position(point.x - this.diff.x, point.y - this.diff.y, { deep: true });
    }
  }

  private reparent(parent: State, parentView: StateView, evt: joint.dia.Event) {
    const point = this.view.paper.clientToLocalPoint(evt.clientX, evt.clientY);

    parentView.unhighlight(parentView.el, { embedding: true });

    const regions = parent.getEmbeddedCells().filter((c) => c instanceof Region);
    const insertIndex = this.getInsertIndex(parent, regions, point);

    this.moveToIndex(insertIndex);
  }

  private findNewParent(evt: dia.Event): StateView | null {
    const point = this.view.paper.clientToLocalPoint(evt.clientX, evt.clientY);
    const views = this.view.paper.findElementViewsAtPoint(point);
    const stateViews = views.filter((v) => v instanceof StateView);
    const region = this.view.model as Region;
    const newParent = stateViews.reduce(
      (highest, current) => {
        const isChildOfRegion = region.getEmbeddedCells({ deep: true }).includes(current.model);
        const state = current.model as State;
        if (isChildOfRegion || state.getSubdiagram()) return highest;
        return !highest || current.model.z() > highest.model.z() ? current : highest;
      },
      null as StateView | null
    );

    if (newParent === null) {
      if (activeView.value !== null) {
        return activeView.value;
      }
    }

    return newParent;
  }

  private getInsertIndex(parent: State, regions: Region[], point: g.Point) {
    if (parent.isVertical()) {
      const topRegion = regions.find((r) => {
        return r.position().y < point.y && r.position().y + r.size().height > point.y;
      });
      return regions.findIndex((r) => r === topRegion) + 1;
    } else {
      const leftRegion = regions.find((r) => {
        return r.position().x < point.x && r.position().x + r.size().width > point.x;
      });
      return regions.findIndex((r) => r === leftRegion) + 1;
    }
  }

  private moveToIndex(insertIndex: number) {
    const state = this.view.model.getParentCell() as State;
    let regions = state.getEmbeddedCells().filter((r) => r instanceof Region);
    state.unembed(regions);
    regions = regions.filter((r) => r.id !== this.view.model.id);
    regions.splice(insertIndex - 1, 0, this.view.model as Region);
    state.embed(regions, { deep: true });
  }

  private removeFromCompositeState(parent: State, parentView: StateView, evt: joint.dia.Event) {
    const region = this.view.model as Region;
    const point = this.view.paper.clientToLocalPoint(evt.clientX, evt.clientY);

    parent.unembed(this.view.model);
    parentView.unhighlight(parentView.el, { embedding: true });
    region.position(point.x - this.diff.x, point.y - this.diff.y, { deep: true });
  }

  private updateZIndex(newParentView: StateView) {
    let z = newParentView.model.z() + 1;
    this.view.model.set("z", z);
    z += 1;
    this.view.model.getEmbeddedCells({ deep: true }).forEach((c, i) => {
      c.set("z", z + i);
    });
  }

  private updateFreetransform() {
    ui.FreeTransform.clear(this.context.paper);
    this.createFreetransform();
  }
}

util.assign(shapes, {
  Region,
  RegionView,
  RegionController,
});
